import React from 'react';
import get from 'lodash.get';
import { Box, FlexBox, Typography, Card, Button, SvgIcon, MuiBox, color } from '@kis-technologies/kis-ui';
import { NOT_CONNECTED, CONNECTED, ERROR, OPERATIONAL, UNKNOWN } from 'constants/hardware';
import { useSelector } from 'react-redux';
import { selectors as kioskSelectors } from 'services/kiosk';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { ActionOverlay } from 'components/ActionOverlay';
import { TicketStockOverlay } from 'components/TicketStockOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { useNavigate } from 'react-router-dom';
import { useStyles } from '../styled';

export interface TicketPrinterProps {
  kioskHardwareId: string;
}

const TicketPrinter: React.FC<TicketPrinterProps> = (props: TicketPrinterProps) => {
  const { kioskHardwareId } = props;

  const navigate = useNavigate();
  // Actions and Selectors
  const kioskPrinters = useSelector(kioskSelectors.selectPrinters);
  const hardwarePrinters = useSelector(hardwareSelectors.selectPrinters);
  const ticketTemplates = useSelector(kioskSelectors.selectTicketTemplates);

  const { pingPrinters, setPrintData, configurePrinter, resetPrinterConfiguredStatus } = useActions(hardwareActions);

  const kioskPrinter = kioskPrinters.find((p) => p.id === kioskHardwareId);
  const hardwarePrinter = hardwarePrinters.find((p) => p.kioskHardwareId === kioskHardwareId);
  const status = hardwarePrinter?.status || { code: UNKNOWN };
  const configuredStatus = hardwarePrinter?.configuredStatus || { completed: false, pending: false };

  const currentStock = get(kioskPrinter, 'values.currentStock', 0);
  const testTicketTemplateId = get(kioskPrinter, 'configuration.testTicketTemplateId');

  const [showTroubleshoot, setShowTroubleshoot] = React.useState(false);
  const [showTicketStock, setShowTicketStock] = React.useState(false);
  const [printerConfigured, setPrinterConfigured] = React.useState(false);
  const [showConfigureError, setShowConfigureError] = React.useState(!!configuredStatus.error);

  const classes = useStyles();

  // So funny story, I have local state and global state
  // here because this effect gets triggered when the global
  // state (configured) changes, but since this effect
  // runs every time the page loads, I also use the local
  // state so it doesn't keep jumping back to this page
  React.useEffect(() => {
    if (configuredStatus.completed && printerConfigured) {
      testPrint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredStatus.completed]);

  React.useEffect(() => {
    setShowConfigureError(!!configuredStatus.error);
  }, [configuredStatus.error]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // Set the print data and re-route to the printing page (PrintingView)
  const testPrint = () => {
    const testTicketTemplate = ticketTemplates.find((template) => template.id === testTicketTemplateId);

    setPrintData({
      printJobs: [
        {
          message: testTicketTemplate ? testTicketTemplate.template : '',
          kioskHardwareId
        }
      ]
    });

    navigate('/printing');
  };

  // Test Print Click
  const onTestPrintClick = () => {
    if (!configuredStatus.completed) {
      // This will kick off the test print after it is done
      configurePrinter({ kioskHardwareId });

      setPrinterConfigured(true);
    } else {
      testPrint();
    }
  };

  const onTroubleshootConfirm = () => {
    pingPrinters({ kioskHardwareId });
    setShowTroubleshoot(false);
  };

  const onConfigureErrorConfirm = () => {
    resetPrinterConfiguredStatus({ kioskHardwareId });
    setShowConfigureError(false);
  };

  // **************
  // **  RENDER  **
  // **************

  const renderTitle = () => {
    let label = 'Printer';
    if (kioskPrinter && kioskPrinter.configuration.displayName) {
      label = `${kioskPrinter.configuration.displayName} Printer`;
    }

    return (
      <Typography fontSize={24} fontWeight={900}>
        {label}
      </Typography>
    );
  };

  const renderMessage = () => {
    if (status.code === OPERATIONAL || (status.code === CONNECTED && currentStock)) {
      return (
        <Typography fontSize={15}>
          The stock count is set to{' '}
          <Typography fontWeight={600} component="span">
            {currentStock}
          </Typography>
          . Please initiate a test print.
        </Typography>
      );
    }
    if (status.code === CONNECTED) {
      return <Typography fontSize={15}>Please load stock and initiate a test print.</Typography>;
    }
    if (status.code === ERROR) {
      return <Typography fontSize={15}>Your printer device is experiencing some issues.</Typography>;
    }
    if (status.code === NOT_CONNECTED) {
      return <Typography fontSize={15}>Your printer device is not connected.</Typography>;
    }
    return null;
  };

  const renderAction = () => {
    if (status.code === OPERATIONAL) return <SvgIcon name="success_circle" size={46} color={color.GREEN} />;
    if (status.code === CONNECTED) return <SvgIcon name="na_circle" size={46} color={color.GRAY} />;
    if (status.code === ERROR || status.code === NOT_CONNECTED) {
      return <SvgIcon name="error_circle" size={46} color={color.RED} />;
    }
    return null;
  };

  const renderButtons = () => {
    if (status.code === CONNECTED || status.code === OPERATIONAL) {
      return (
        <FlexBox direction="column" flexOverride="none" gutter={2}>
          <Box flexOverride="none">
            <Button
              customSize="medium"
              appearance={!currentStock ? 'primary' : 'white'}
              fullWidth
              onClick={() => setShowTicketStock(true)}
              data-testid="stock-button"
            >
              {!currentStock ? 'Load Stock' : 'Update Stock'}
            </Button>
          </Box>
          <Box flexOverride="none">
            <Button
              customSize="medium"
              appearance="primary"
              disabled={!currentStock}
              fullWidth
              onClick={onTestPrintClick}
              data-testid="test-print-button"
            >
              Test Print
            </Button>
          </Box>
        </FlexBox>
      );
    }
    if (status.code === ERROR || status.code === NOT_CONNECTED) {
      return (
        <Button customSize="medium" appearance="error" fullWidth onClick={() => setShowTroubleshoot(true)}>
          Resolve
        </Button>
      );
    }
    return null;
  };

  return (
    <>
      {configuredStatus.pending && (
        <LoadingOverlay message="Please wait while we configure your printer for the first time" />
      )}
      {showConfigureError && (
        <ActionOverlay
          title={
            <Typography variant="h1" align="center">
              Oh no!
            </Typography>
          }
          description={
            <Box width={600}>
              <Typography align="center" fontSize={30}>
                There was an error configuring your printer. Please try again or contact KIS if the issue persists
              </Typography>
            </Box>
          }
          confirmText="OK"
          onConfirm={onConfigureErrorConfirm}
        />
      )}
      <MuiBox className={classes.systemCheckHardwareDevices}>
        <Card fullHeight hasBorderRadius raised>
          <Card.Title gutter={5} title={renderTitle()} action={renderAction()} />
          <Card.Content gutter={5}>
            <Box flexOverride="1">{renderMessage()}</Box>

            {renderButtons()}
          </Card.Content>
        </Card>
      </MuiBox>

      {showTroubleshoot && (
        <ActionOverlay
          title="The system is experiencing an issue."
          description="Please contact your admin at this time for support. It is recommended you take the kiosk inactive while this issue is resolved."
          cancelText="Close"
          confirmText="Retest"
          onConfirm={onTroubleshootConfirm}
          onCancel={() => setShowTroubleshoot(false)}
        />
      )}

      {showTicketStock && (
        <TicketStockOverlay
          onCancel={() => setShowTicketStock(false)}
          onConfirm={() => setShowTicketStock(false)}
          kioskHardwareId={kioskHardwareId}
        />
      )}
    </>
  );
};

export default TicketPrinter;
